import { Fragment } from "react";
import { Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { XMarkIcon } from "~/ui";
import { toastBackgroundColors, toastForegroundColors } from "./toastColors";
import { toastIcons } from "./toastIcons";
import type { Toast } from "./toastStore";

export interface ToastMessageProps {
  toast: Toast;
  onClose: () => void;
}

export const ToastMessage = ({ toast, onClose }: ToastMessageProps) => {
  const { t } = useTranslation();

  return (
    <Transition
      show={toast.state === "open"}
      appear
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition-all ease-in duration-500"
      leaveFrom="opacity-100 max-h-52"
      leaveTo="opacity-0 [&:not(:last-child)]:max-h-0"
    >
      <div
        onClick={onClose}
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        className={twMerge(
          "pointer-events-auto z-50 w-full max-w-toast overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5",
          toastBackgroundColors[toast.type],
          toastForegroundColors[toast.type],
        )}
      >
        <div className="px-5 py-4">
          <div className="flex items-start gap-4">
            <div className={twMerge("mt-1 flex-shrink-0")}>
              {toast.icon ? toast.icon : toastIcons[toast.type]}
            </div>

            <div className="flex w-0 flex-1 flex-col gap-1 pt-0.5">
              {toast.title && <p className="font-medium">{toast.title}</p>}

              <p className="leading-5">{toast.message}</p>
            </div>

            <div className="ml-4 flex flex-shrink-0">
              <button
                type="button"
                className="inline-flex rounded-md hover:opacity-70 focus:outline-none focus:ring-1 focus:ring-white"
                onClick={onClose}
              >
                <span className="sr-only">{t("layout.close_toast")}</span>

                <XMarkIcon className="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};
